/**
 Author: Revelation.AF
 Engine: [//]
 Git: nusktec
 **/

import moment from "moment-timezone";
import { toast } from "react-toastify";
import { LoginPayload } from "../services/_model";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DEFAULT_TIMEZONE = "Africa/Lagos";

/** Flatten time-group times whether stored as flat slots, nested pairs, or JSON string. */
export const flattenTimeGroupSlots = (times: any): any[] => {
    let parsed = times;
    if (typeof parsed === "string") {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(parsed) || !parsed.length) return [];
    if (Array.isArray(parsed[0])) return parsed.flat().filter(Boolean);
    return parsed;
};

/**
 * Convert time-group slots authored in Africa/Lagos into the parent's timezone
 * so day/time pickers show local wall-clock values.
 */
export const convertTimeGroupSlotsToTimezone = (
    slots: any[],
    parentTimezone?: string | null
) => {
    const tz = parentTimezone || DEFAULT_TIMEZONE;
    const flat = flattenTimeGroupSlots(slots);

    return flat.map((slot) => {
        const dayText = slot.dayText || DAY_NAMES[Number(slot.day)] || "Monday";
        const timeText = slot.timeText || `${Number(slot.timex ?? slot.time ?? 0)}:00`;
        let time = moment.tz(
            `${timeText} ${dayText}`,
            ["h:mmA dddd", "hA dddd", "h:mm A dddd"],
            DEFAULT_TIMEZONE
        );
        if (!time.isValid()) {
            const hour = Number(slot.timex ?? slot.time ?? 0);
            time = moment
                .tz(DEFAULT_TIMEZONE)
                .day(Number(slot.day) || 0)
                .hour(hour)
                .minute(0)
                .second(0);
        }
        const converted = tz === DEFAULT_TIMEZONE ? time : time.clone().tz(tz);
        return {
            day: converted.day(),
            dayText: DAY_NAMES[converted.day()],
            dayAbbr: DAY_ABBR[converted.day()],
            timex: converted.hour(),
            time: converted.hour(),
            timeText: converted.format("h:mmA"),
        };
    });
};

export const restructureTimeData = (data: { day: any; dayText: any; dayAbbr: any; timex: any; timeText: any; }[]) => {

    if (!data[0]?.dayAbbr) return []

    const sortedData: any[] = [];
    // Iterate through each item in the data array
    data?.forEach((item: { day: any; dayText: any; dayAbbr: any; timex: any; timeText: any; }) => {
        // Check if the day already exists in the sortedData array
        let dayEntry = sortedData.find(dayObj => dayObj.day === item.day);

        // If the day does not exist, create a new entry for it
        if (!dayEntry) {
            dayEntry = {
                day: item.day,
                dayText: item.dayText,
                dayAbbr: item.dayAbbr,
                times: []
            };
            sortedData.push(dayEntry);
        }

        // Add the current time slot to the day's times array
        dayEntry?.times?.push({
            time: item.timex,
            timeText: item.timeText
        });
    });

    // Sort the sortedData array by 'day' and each 'times' array by 'time'
    sortedData.sort((a, b) => a.day - b.day);
    sortedData.forEach(dayObj => {
        dayObj.times.sort((a: { time: number; }, b: { time: number; }) => a.time - b.time);
    });

    return sortedData;
}


export const filterDaysOfTheWeek = (data: any) => {
    let xdayArr = []
    // extracted dayText and day values and saved them as 'name' and 'value' respectively for ease of use in the custom dropdown component
    for (let i = 0; i < data.length; i++) {
        let name = data[i].dayText;
        let value = data[i].day;
        let dx = { name, value };
        xdayArr.push(dx);
    }
    return xdayArr;
}

export const calculateExactAge = (registeredAge: any, registrationDate: any) => {
    const registeredDate = new Date(registrationDate);
    const now = new Date();

    // Calculate the difference in years
    const yearDiff = now.getFullYear() - registeredDate.getFullYear();

    // Check if birthday hasn't occurred yet this year
    const monthDiff = now.getMonth() - registeredDate.getMonth();
    const dayDiff = now.getDate() - registeredDate.getDate();

    // If birthday hasn't occurred yet, subtract 1 year
    const adjustedYearDiff = (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0))
        ? yearDiff - 1
        : yearDiff;

    // Calculate and return exact age
    return registeredAge + adjustedYearDiff;
};

export interface LoginResult {
  decoded: LoginPayload | null;
  status: boolean;
}

export function toBase64Url(payload: unknown): string {
  const json = JSON.stringify(payload);
  const bytes = new TextEncoder().encode(json);
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  const base64 = btoa(binary);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function fromBase64Url(base64: string): string {
  const normalized = base64.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function validateLoginCode(code: string): LoginResult {
  if (!code) {
    toast.error("Code is required");
    return { decoded: null, status: false };
  }

  let decoded: LoginPayload | null = null;
  let status = true;

  try {
    const jsonStr = fromBase64Url(code);
    decoded = JSON.parse(jsonStr);
  } catch {
    status = false;
  }

  if (!decoded) {
    return { decoded: null, status: false };
  }

  const { adminToken, parentId, timestamp } = decoded;

  if (!adminToken || !parentId || !timestamp) {
    status = false;
  }

  // 3 min (180000 ms) expiration
  if (Date.now() - timestamp > 180000) {
    status = false;
  }

  return { decoded, status };
}
